import React, { useEffect, useState } from 'react';
import { lazy } from 'react-loosely-lazy';
import {
	type CommandPaletteModalProps,
	CommandPaletteWrapper,
	useCommandPaletteIsOpen,
} from '@atlassian/jira-command-palette';
import { useCommandPaletteShortcut } from '@atlassian/jira-command-palette-common';
import { Shortcuts } from '@atlassian/jira-common-components-keyboard-shortcuts';
import Placeholder from '@atlassian/jira-placeholder';
import { usePageLoadedSubscriber } from '@atlassian/jira-spa-state-controller';
import UFOSegment from '@atlassian/jira-ufo-segment';
import { JiraCommandPaletteErrorBoundary } from './common/ui/error-boundary';

/* istanbul ignore next */
// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const JiraCommandPaletteLazy = lazy(() =>
	import(/* webpackChunkName: "async-command-palette-integration" */ './ui').then(
		(module) => module.CommandPaletteIntegration,
	),
);

const InternalAsyncCommandPalette = (props: CommandPaletteModalProps) => {
	const [initialised, setInitialised] = useState(false);

	const [{ isOpen }] = useCommandPaletteIsOpen();
	const { keyMap: commandPaletteKeyMap } = useCommandPaletteShortcut();

	const [{ hasLoaded }] = usePageLoadedSubscriber();

	useEffect(() => {
		if (!initialised && (isOpen || hasLoaded)) setInitialised(true);
	}, [initialised, isOpen, hasLoaded]);

	return (
		<UFOSegment name="command-palette">
			<Shortcuts keyMap={commandPaletteKeyMap} />

			{initialised && (
				<Placeholder name="jira-command-palette" fallback={<></>}>
					<JiraCommandPaletteLazy {...props} />
				</Placeholder>
			)}
		</UFOSegment>
	);
};

export const AsyncJiraCommandPalette = (props: CommandPaletteModalProps) => (
	<JiraCommandPaletteErrorBoundary id="jira-command-palette-integration-async">
		<CommandPaletteWrapper>
			<InternalAsyncCommandPalette {...props} />
		</CommandPaletteWrapper>
	</JiraCommandPaletteErrorBoundary>
);
